import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import FieldRenderer from './FieldRenderer';
import { Button } from '../../components/buttons/button';
import { ICONS } from '../../constants/imageConstants';
import { useFormEntry } from '../../hooks/useFormEntry';
import HelpTooltip from '../../components/ui/HelpTooltip';
import PageLayout from '../../components/layout/PageLayout';
import EmptyState from '../../components/data-display/EmptyState';
import LocationPermissionModal from "../../components/modals/LocationPermissionModal";
import { checkRelevance, interpolateLabel } from '../../utils/calculationEngine';
import EntitySelectionPage from './components/EntitySelectionPage';

const SectionIcon = () => (
  <svg className="w-5 h-5 text-blue-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const HIDDEN_FIELD_TYPES = new Set([
  'enumerator', 'hidden', 'deviceid', 'username',
  'start', 'end', 'subscriberid', 'simserial', 'phonenumber', 'caseid', 'audit'
]);

const SuccessIcon = () => (
  <svg className="w-4 h-4 text-green-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const SuccessMessage = React.memo(({ message, onComplete }) => {
  const [opacity, setOpacity] = React.useState('opacity-100');
  
  React.useEffect(() => {
    const timer = setTimeout(() => setOpacity('opacity-0'), 500);
    const cleanup = setTimeout(onComplete, 1000);
    return () => {
      clearTimeout(timer);
      clearTimeout(cleanup);
    };
  }, [onComplete]);

  return (
    <div className={`flex items-start gap-2 mt-2 p-2.5 bg-green-50/50 border border-green-200 rounded-lg transition-opacity duration-500 ${opacity}`}>
       <SuccessIcon />
       <p className="text-xs text-green-700 font-semibold leading-relaxed">{message}</p>
    </div>
  );
});

const SubmitIcon = () => (
  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const RenderSection = React.memo(({
  section,
  formValues,
  onChange,
  activeRepeats,
  errors,
  hasAttemptedSubmit,
  nameValues,
  fileUploadStatus,
  setFileUploadStatus,
  visibleFormulas,
  setVisibleFormulas,
  parentPath = '',
  readOnlyFields = {}
}) => {
  const [successFields, setSuccessFields] = useState({});
  const prevErrorsRef = useRef(errors);

  React.useEffect(() => {
    const newSuccesses = {};
    let changed = false;

    // Detect fields that went from invalid to valid (constraints only)
    Object.keys(prevErrorsRef.current || {}).forEach(name => {
      const prevError = prevErrorsRef.current[name];
      if (prevError?.type === 'constraint' && !errors[name]) {
        newSuccesses[name] = prevError.message;
        changed = true;
      }
    });

    if (changed) {
      setSuccessFields(prev => ({ ...prev, ...newSuccesses }));
    }
    prevErrorsRef.current = errors;
  }, [errors, hasAttemptedSubmit]);

  const handleSuccessComplete = useCallback((name) => {
    setSuccessFields(prev => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  if (!section) return null;
  if (section.relevance && !checkRelevance(section, nameValues, parentPath)) {
    return null;
  }

  const isRepeat = !!section.is_repeat;
  const isRoot = section.name === '__root__';
  const sectionDisplayName = isRoot ? 'General Section' : (section.label || section.name?.replace(/_/g, ' ').replace(/^grp /i, ''));

  const renderFields = (currentPath = parentPath) => {
    const allItems = [
      ...(section.fields || []).map(f => ({ ...f, _isField: true })),
      ...(section.sections || []).map(s => ({ ...s, _isSection: true }))
    ].sort((a, b) => (a.order || 0) - (b.order || 0));

    let fieldCounter = 0;

    return (
      <div className="space-y-6">
        {allItems.map(item => {
          if (item._isField) {
            const name = currentPath ? `${currentPath}${item.name}` : item.name;
            const type = item.type?.toLowerCase();
            if (HIDDEN_FIELD_TYPES.has(type)) return null;
            if (item.relevance && !checkRelevance(item, nameValues, currentPath)) return null;

            const isNote = type === 'note';
            if (!isNote) fieldCounter++;

            return (
              <div key={name} data-field-name={name}>
                {!isNote && (
                <div className="mb-2.5">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                    <span className="text-gray-400 font-bold">{fieldCounter}.</span>
                    {interpolateLabel(item.label || item.name, nameValues, currentPath, true)}
                    {item.is_required && <span className="text-red-500 text-base">*</span>}
                    {item.help_text && <HelpTooltip helpText={item.help_text} />}
                    {(item.calculate || item.calculation) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setVisibleFormulas(prev => ({ ...prev, [name]: !prev[name] }));
                        }}
                        className={`shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full border transition-all duration-200 ${
                          visibleFormulas[name]
                            ? 'bg-green-100 border-green-200 text-green-700 shadow-inner'
                            : 'bg-gray-50 border-gray-200 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                        }`}
                        title="Show calculation logic"
                      >
                        <span className="text-[10px] font-black font-serif">?</span>
                      </button>
                    )}
                  </label>
                </div>
                )}
                <FieldRenderer
                    field={{ ...item, id: name }}
                    value={formValues[name]}
                    onChange={value => onChange(name, value)}
                    uploadStatus={fileUploadStatus[name]}
                    setUploadStatus={(status) => setFileUploadStatus(prev => ({ ...prev, [name]: status }))}
                    nameValues={nameValues}
                    error={errors[name]}
                    hasAttemptedSubmit={hasAttemptedSubmit}
                    readOnly={!!readOnlyFields[name]}
                />
                {(item.calculate || item.calculation) && visibleFormulas[name] && (
                  <div className="mt-2 p-3 bg-green-50/50 border border-green-200 border-dashed rounded-lg w-full animate-fade-in">
                    <code className="text-[12px] font-mono text-green-700 bg-white px-2 py-1.5 rounded shadow-sm border border-green-100/50 block break-all whitespace-pre-wrap">
                      {item.calculate || item.calculation}
                    </code>
                  </div>
                )}
                {errors[name] && (errors[name].type !== 'required' || hasAttemptedSubmit) && (
                  <div className="flex items-start gap-2 mt-2 p-2.5 bg-red-50 border border-red-100 rounded-lg animate-shake">
                    <svg className="w-4 h-4 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-xs text-red-600 font-semibold leading-relaxed">{errors[name].message}</p>
                  </div>
                )}
                {successFields[name] && (
                  <SuccessMessage 
                    message={successFields[name]} 
                    onComplete={() => handleSuccessComplete(name)} 
                  />
                )}
              </div>
            );
          }

          if (item._isSection) {
            return (
              <RenderSection
                key={item.id}
                section={item}
                formValues={formValues}
                onChange={onChange}
                activeRepeats={activeRepeats}
                errors={errors}
                hasAttemptedSubmit={hasAttemptedSubmit}
                nameValues={nameValues}
                fileUploadStatus={fileUploadStatus}
                setFileUploadStatus={setFileUploadStatus}
                visibleFormulas={visibleFormulas}
                setVisibleFormulas={setVisibleFormulas}
                parentPath={currentPath}
                readOnlyFields={readOnlyFields}
              />
            );
          }

          return null;
        })}
      </div>
    );
  };

  const repeatCount = parseInt(section.repeat_count, 10);
  const isMultiRepeat = isRepeat && repeatCount >= 2;
  const isSingleRepeat = isRepeat && repeatCount === 1;

  if (isMultiRepeat) {
    const key = parentPath ? `${parentPath}${section.name}` : section.name;
    const count = activeRepeats[key] || 0;
    const sectionPath = parentPath ? `${parentPath}${section.name}_` : `${section.name}_`;

    return (
      <div className="bg-gray-50/50 p-3 rounded-lg border border-gray-200 mb-6">
        <div className="flex items-center justify-between pb-2 border-b border-gray-200 mb-4">
          <div className="flex items-center gap-2">
            <SectionIcon />
            <div>
              <h3 className="text-sm font-bold text-gray-900 capitalize">{sectionDisplayName}</h3>
              {section.description && <p className="text-xs text-gray-500">{section.description}</p>}
            </div>
          </div>
          {section.repeat_count && (
            <div 
              className="flex items-center justify-center min-w-[28px] h-7 px-1.5 bg-blue-50 text-blue-700 font-bold text-xs rounded-full border border-blue-200"
              title="Repeat Count"
            >
              {section.repeat_count}
            </div>
          )}
        </div>
        {count > 0 && (
          <div className="space-y-4">
            {[...Array(count)].map((_, idx) => (
              <div key={idx} className="rounded-lg">
                <div className="inline-flex items-center justify-between mb-4 px-2.5 py-1 bg-blue-50 border border-blue-100 rounded-md">
                  <div className="text-[11px] font-extrabold text-blue-700 uppercase tracking-widest">
                    {sectionDisplayName} #{idx + 1}
                  </div>
                </div>
                 {renderFields(`${sectionPath}${idx}_`)}
               </div>
             ))}
           </div>
         )}
       </div>
     );
   }

   const path = isSingleRepeat 
     ? (parentPath ? `${parentPath}${section.name}_0_` : `${section.name}_0_`)
     : parentPath;

   const content = renderFields(path);
   if (!content) return null;

   return (
     <div className="mb-10 last:mb-0">
       <div className="mb-6 flex items-center gap-2.5 border-b-2 border-gray-100 pb-3">
         <SectionIcon />
         <div>
           <h2 className="text-lg font-bold text-gray-900 capitalize">{sectionDisplayName}</h2>
           {section.description && <p className="text-xs text-gray-500 mt-1">{section.description}</p>}
         </div>
       </div>
       {content}
     </div>
   );
});

const FormEntry = () => {
  const { formId, submissionId } = useParams();
  const {
    form: survey,
    status,
    formValues,
    setFormValues,
    nameValues,
    activeRepeats,
    errors,
    hasAttemptedSubmit,
    submissionStatus,
    submittingAction,
    handleFieldChange,
    handleSubmit,
    retryFormLoad,
    fileUploadStatus,
    setFileUploadStatus,
    locationData,
    isLocationLoading,
    refreshLocation,
    permissionDenied,
    positionUnavailable,
    entitySelected,
    setEntitySelected,
    setSearchParams,
    readOnlyFields,
    setReadOnlyFields,
    isConfirmed,
    setIsConfirmed,
    isSubmitDisabled
  } = useFormEntry(formId, submissionId);

  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissionErrorType, setPermissionErrorType] = useState('denied');
  const [visibleFormulas, setVisibleFormulas] = useState({});

  const isLoading = status === 'loading' && !survey;
  const hasCoordinates = locationData?.latitude && locationData?.longitude;

  const handleEntitySelected = useCallback((entity) => {
    // Persist selection and confirmation in URL
    setSearchParams(params => {
      params.set('entity_id', entity.id);
      params.set('confirmed', 'true');
      params.delete('tab');
      return params;
    }, { replace: true });

    setEntitySelected(entity);
    setIsConfirmed(true);
    // Form pre-fill is handled by the mode-aware effect in useFormEntry (prefillDoneRef)
  }, [setEntitySelected, setSearchParams, setIsConfirmed]);

  const handleRetryLocation = () => {
    refreshLocation()?.catch(err => {
        if (err) {
          if (err.code === 'PERMISSION_DENIED') {
            setPermissionErrorType('denied');
            setShowPermissionModal(true);
          } else if (err.code === 'POSITION_UNAVAILABLE') {
            setPermissionErrorType('unavailable');
            setShowPermissionModal(true);
          }
        }
      });
  };
  
  if (status === 'failed' || (status !== 'loading' && !survey)) {
    return (
      <PageLayout title="Form" showBackButton showStatus>
        <div className="flex flex-col items-center justify-center min-h-[50vh]">
          <EmptyState 
            title="Form Not Available"
            message="We couldn't load this Form. It may have been removed, or there might be a connection issue."
          />
          <div className="flex gap-2 mt-6 w-full max-w-xs">
            <Button onClick={retryFormLoad} variant="primary" size="lg" fullWidth>Try Again</Button>
            <Button onClick={() => window.history.back()} variant="secondary" size="lg" fullWidth>Go Back</Button>
          </div>
        </div>
      </PageLayout>
    );
  }
  
  if (isLoading) {
    return (
      <PageLayout title="Loading..." showStatus>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-pulse text-gray-400 font-medium">Loading form details...</div>
        </div>
      </PageLayout>
    );
  }

  // Linked Record Flow
  const requiresEntity = survey?.linked_entity_type_id && survey?.entity_action_default;
  
  if (requiresEntity && !isConfirmed && !submissionId) {
    const recordIdFromUrl = new URLSearchParams(window.location.search).get('entity_id');
    const handleSelectionBack = recordIdFromUrl
      ? () => setSearchParams(params => { params.delete('entity_id'); return params; }, { replace: true })
      : undefined;

    return (
      <PageLayout title="Record Selection" showBackButton onBack={handleSelectionBack}>
        <EntitySelectionPage
          form={survey}
          onEntitySelected={handleEntitySelected}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout title={survey?.title || 'Form Entry'} showStatus>
      <LocationPermissionModal 
        isOpen={showPermissionModal} 
        onClose={() => setShowPermissionModal(false)}
        type={permissionErrorType}
      />

      {!hasCoordinates && (
        <div className={`border px-4 py-3.5 rounded-lg mb-6 flex items-center justify-between gap-3 transition-all ${permissionDenied || positionUnavailable ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
          <span className="text-sm font-semibold">
            {isLocationLoading ? 'Fetching coordinates...' : permissionDenied ? 'Location blocked.' : positionUnavailable ? 'GPS off.' : 'Enable GPS.'}
          </span>
          {!isLocationLoading && (
            <button 
              onClick={handleRetryLocation} 
              className="text-xs font-bold underline hover:no-underline active:scale-95 transition-transform"
            >
              Retry
            </button>
          )}
        </div>
      )}

      <form onSubmit={(e) => e.preventDefault()}>
        <div className="space-y-4 pb-6">
          {(survey?.sections || []).map((section) => (
            <RenderSection
              key={section.id}
              section={section}
              formValues={formValues}
              onChange={handleFieldChange}
              activeRepeats={activeRepeats}
              errors={errors}
              hasAttemptedSubmit={hasAttemptedSubmit}
              nameValues={nameValues}
              fileUploadStatus={fileUploadStatus}
              setFileUploadStatus={setFileUploadStatus}
              visibleFormulas={visibleFormulas}
              setVisibleFormulas={setVisibleFormulas}
              readOnlyFields={readOnlyFields}
            />
          ))}
        </div>
        
        <div className="mt-8 pt-6 border-t-2 border-gray-200 flex gap-3 pb-4">
          {submissionId ? (
            <Button
              variant="primary"
              fullWidth
              size="large"
              isLoading={submissionStatus === 'submitting'}
              disabled={isSubmitDisabled}
              onClick={() => handleSubmit('close')}
              className="h-12 font-bold"
            >
              <SubmitIcon />
              <span className="ml-2">{submissionStatus === 'submitting' ? 'Saving...' : 'Save Changes'}</span>
            </Button>
          ) : (
            <>
              <Button
                onClick={() => handleSubmit('close')}
                disabled={submissionStatus === 'submitting'}
                variant="secondary"
                fullWidth
                className="h-12"
              >
                <SubmitIcon />
                <span className="ml-2">{submissionStatus === 'submitting' && submittingAction === 'close' ? 'Submitting...' : 'Submit & Close'}</span>
              </Button>
              <Button
                onClick={() => handleSubmit('new')}
                disabled={submissionStatus === 'submitting'}
                variant="primary"
                fullWidth
                className="h-12"
              >
                <SubmitIcon />
                <span className="ml-2">{submissionStatus === 'submitting' && submittingAction === 'new' ? 'Submitting...' : 'Submit & New'}</span>
              </Button>
            </>
          )}
        </div>
      </form>
    </PageLayout>
  );
};

export default FormEntry;